import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import BreadcrumbTrail from '../../components/ui/BreadcrumbTrail';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { CheckCircle, XCircle, Clock, Upload, RefreshCw, AlertCircle, FileImage, X } from 'lucide-react';
import { isSuperAdmin, isGroupAdmin, getDisplayRole } from '../../utils/roles';

interface PaymentData {
  id: string;
  amount_vatu: number;
  payment_reference: string;
  payment_status: 'pending' | 'approved' | 'rejected';
  payment_proof_url: string | null;
  submitted_at: string;
  rejection_reason: string | null;
  approved_at: string | null;
  group_id?: string;
  group_name?: string;
  plan?: {
    plan_name: string;
    price_vatu: number;
  };
}

interface PaymentSummary {
  totalAmount: number;
  paymentCount: number;
  payments: PaymentData[];
}

const PaymentStatusPage = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { paymentId } = (location.state as { paymentId?: string }) || {};

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  
  const [paymentData, setPaymentData] = useState<PaymentData | null>(null);
  const [paymentSummary, setPaymentSummary] = useState<PaymentSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [receiptPreview, setReceiptPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [lastUpdateTime, setLastUpdateTime] = useState<Date>(new Date());
  
  const isGroupCreator = userProfile?.role === 'group_creator';

  const isSuperAdminUser = isSuperAdmin(userProfile?.role);
  const userRole = getDisplayRole(userProfile?.role);

  useEffect(() => {
    if (user) {
      loadPaymentData();
      setupRealtimeSubscription();
    }
  }, [user, paymentId, isGroupCreator]);

  const loadPaymentData = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      // For group creators, fetch all pending payments and show total
      if (isGroupCreator && !paymentId) {
        const { data: allPayments, error: fetchError } = await supabase
          .from('payment_transactions')
          .select(`
            *,
            group:groups (
              id,
              name
            ),
            subscription:user_subscriptions (
              plan:subscription_plans (
                plan_name,
                price_vatu
              )
            )
          `)
          .eq('user_id', user.id)
          .eq('payment_status', 'pending')
          .order('submitted_at', { ascending: false });

        if (fetchError) throw fetchError;

        if (allPayments && allPayments.length > 0) {
          const payments: PaymentData[] = allPayments.map((p: any) => ({
            id: p.id,
            amount_vatu: Number(p.amount_vatu),
            payment_reference: p.payment_reference || 'N/A',
            payment_status: p.payment_status,
            payment_proof_url: p.payment_proof_url,
            submitted_at: p.submitted_at,
            rejection_reason: p.rejection_reason,
            approved_at: p.approved_at,
            group_id: p.group_id,
            group_name: p.group?.name,
            plan: p.subscription?.plan as any,
          }));

          const totalAmount = payments.reduce((sum, p) => sum + p.amount_vatu, 0);
          
          console.log('[PaymentStatus] Setting paymentSummary:', {
            totalAmount,
            paymentCount: payments.length,
            payments: payments.map(p => ({ id: p.id, amount: p.amount_vatu, group: p.group_name }))
          });
          
          setPaymentSummary({
            totalAmount,
            paymentCount: payments.length,
            payments,
          });

          // Set the first payment as the primary payment data for display
          setPaymentData(payments[0]);
        } else {
          setPaymentData(null);
          setPaymentSummary(null);
        }
      } else {
        // For single payment view or non-group-creators
        let query = supabase
          .from('payment_transactions')
          .select(`
            *,
            group:groups (
              id,
              name
            ),
            subscription:user_subscriptions (
              plan:subscription_plans (
                plan_name,
                price_vatu
              )
            )
          `)
          .eq('user_id', user.id)
          .order('submitted_at', { ascending: false })
          .limit(1);

        if (paymentId) {
          query = query.eq('id', paymentId);
        } else {
          query = query.eq('payment_status', 'pending');
        }

        const { data, error: fetchError } = await query.maybeSingle();

        if (fetchError && fetchError.code !== 'PGRST116') throw fetchError;

        if (data) {
          const formatted: PaymentData = {
            id: data.id,
            amount_vatu: Number(data.amount_vatu),
            payment_reference: data.payment_reference || 'N/A',
            payment_status: data.payment_status,
            payment_proof_url: data.payment_proof_url,
            submitted_at: data.submitted_at,
            rejection_reason: data.rejection_reason,
            approved_at: data.approved_at,
            group_id: data.group_id,
            group_name: data.group?.name,
            plan: data.subscription?.plan as any,
          };
          setPaymentData(formatted);
          setPaymentSummary(null);
        }
      }
    } catch (err: any) {
      console.error('Error loading payment data:', err);
      setError(err.message || 'Failed to load payment data');
    } finally {
      setLoading(false);
    }
  };

  const setupRealtimeSubscription = () => {
    if (!user || !paymentData) return;

    const channel = supabase
      .channel('payment-status')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'payment_transactions',
          filter: `id=eq.${paymentData.id}`,
        },
        () => {
          loadPaymentData();
          setLastUpdateTime(new Date());
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  const handleCheckAgain = async () => {
    setSyncing(true);
    await loadPaymentData();
    setLastUpdateTime(new Date());
    setSyncing(false);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
        setError('Please upload an image (JPG, PNG) or PDF file');
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB');
        return;
      }

      setReceiptFile(file);
      setError(null);

      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setReceiptPreview(reader.result as string);
        };
        reader.readAsDataURL(file);
      } else {
        setReceiptPreview(null);
      }
    }
  };

  const handleRemoveFile = () => {
    setReceiptFile(null);
    setReceiptPreview(null);
  };

  const handleUploadReceipt = async () => {
    if (!user || !receiptFile) {
      setError('Please select a receipt file');
      return;
    }

    try {
      setUploading(true);
      setError(null);
      setSuccessMessage(null);

      const timestamp = Date.now();
      const fileExt = receiptFile.name.split('.').pop();
      const fileName = `receipt_${timestamp}.${fileExt}`;
      const filePath = `${user.id}/${fileName}`;

      // For group creators with multiple payments, upload receipt for all pending payments
      const paymentsToUpdate = paymentSummary && paymentSummary.paymentCount > 1
        ? paymentSummary.payments.filter(p => p.payment_status === 'pending' && !p.payment_proof_url)
        : paymentData && paymentData.payment_status === 'pending' && !paymentData.payment_proof_url
          ? [paymentData]
          : [];

      if (paymentsToUpdate.length === 0) {
        setError('No pending payments found to upload receipt for');
        setUploading(false);
        return;
      }

      console.log('[PaymentStatus] Uploading receipt for', paymentsToUpdate.length, 'payment(s)');
      console.log('[PaymentStatus] File path:', filePath);
      console.log('[PaymentStatus] File name:', receiptFile.name);
      console.log('[PaymentStatus] File size:', receiptFile.size, 'bytes');
      console.log('[PaymentStatus] File type:', receiptFile.type);

      // Upload receipt to Supabase Storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('payment-receipts')
        .upload(filePath, receiptFile, {
          cacheControl: '3600',
          upsert: false,
        });

      if (uploadError) {
        console.error('[PaymentStatus] Storage upload error:', uploadError);
        console.error('[PaymentStatus] Error details:', {
          message: uploadError.message,
          statusCode: uploadError.statusCode,
          error: uploadError.error,
        });
        
        // Provide more specific error messages
        let errorMessage = 'Failed to upload receipt. ';
        if (uploadError.message?.includes('Bucket not found')) {
          errorMessage += 'Storage bucket not found. Please contact support.';
        } else if (uploadError.message?.includes('new row violates row-level security')) {
          errorMessage += 'Permission denied. Please ensure you are logged in and have permission to upload receipts.';
        } else if (uploadError.message?.includes('File size')) {
          errorMessage += 'File is too large. Maximum size is 5MB.';
        } else if (uploadError.message?.includes('MIME type')) {
          errorMessage += 'File type not allowed. Please upload JPG, PNG, or PDF files only.';
        } else {
          errorMessage += uploadError.message || 'Please try again.';
        }
        
        throw new Error(errorMessage);
      }

      console.log('[PaymentStatus] Upload successful:', uploadData);

      console.log('[PaymentStatus] File uploaded successfully, updating payment transactions...');

      // Update all payment transactions with receipt path
      const paymentIds = paymentsToUpdate.map(p => p.id);
      const { error: updateError } = await supabase
        .from('payment_transactions')
        .update({
          payment_proof_url: filePath,
          updated_at: new Date().toISOString(),
        })
        .in('id', paymentIds);

      if (updateError) {
        console.error('[PaymentStatus] Update error:', updateError);
        throw updateError;
      }

      console.log('[PaymentStatus] Receipt uploaded and payments updated successfully');

      // Show success message
      setSuccessMessage(
        paymentsToUpdate.length > 1
          ? `Receipt uploaded successfully for ${paymentsToUpdate.length} payments!`
          : 'Receipt uploaded successfully!'
      );

      // Reload payment data
      await loadPaymentData();
      setReceiptFile(null);
      setReceiptPreview(null);

      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
    } catch (err: any) {
      console.error('[PaymentStatus] Error uploading receipt:', err);
      setError(err.message || 'Failed to upload receipt. Please try again.');
      setSuccessMessage(null);
    } finally {
      setUploading(false);
    }
  };

  const handleCancelPayment = () => {
    if (confirm('Are you sure you want to cancel this payment submission?')) {
      navigate('/payment-instructions');
    }
  };

  const handleResubmitPayment = () => {
    navigate('/payment-instructions');
  };

  const handleChangePlan = () => {
    navigate('/subscription-management');
  };

  const getStatusIcon = () => {
    switch (paymentData?.payment_status) {
      case 'approved':
        return <CheckCircle className="w-12 h-12 text-success" />;
      case 'rejected':
        return <XCircle className="w-12 h-12 text-destructive" />;
      default:
        return <Clock className="w-12 h-12 text-warning" />;
    }
  };

  const getStatusColor = () => {
    switch (paymentData?.payment_status) {
      case 'approved':
        return 'bg-success/10 border-success/20 text-success';
      case 'rejected':
        return 'bg-destructive/10 border-destructive/20 text-destructive';
      default:
        return 'bg-warning/10 border-warning/20 text-warning';
    }
  };

  const getStatusText = () => {
    switch (paymentData?.payment_status) {
      case 'approved':
        return 'Payment Approved';
      case 'rejected':
        return 'Payment Rejected';
      default:
        return 'Payment Pending';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-muted-foreground">Loading payment status...</div>
      </div>
    );
  }

  if (!paymentData) {
    return (
      <div className="min-h-screen bg-background">
        <Header
          onMenuToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          isMobileMenuOpen={isMobileMenuOpen}
        />
        <div className="flex">
          <div className="hidden lg:block">
            <Sidebar
              isCollapsed={isSidebarCollapsed}
              userRole={userRole}
              isSuperAdmin={isSuperAdminUser}
            />
          </div>
          <main className={`flex-1 transition-layout ${isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
            <div className="p-6 max-w-4xl mx-auto">
              <div className="bg-card rounded-lg border border-border p-12 text-center">
                <Icon name="FileText" size={48} className="mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground mb-4">No payment found</p>
                <Button
                  onClick={() => navigate('/payment-instructions')}
                  variant="default"
                >
                  Make Payment
                </Button>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header
        onMenuToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        isMobileMenuOpen={isMobileMenuOpen}
      />

      <div className="flex pt-20">
        <div className="hidden lg:block">
          <Sidebar
            isCollapsed={isSidebarCollapsed}
            userRole={userRole}
            isSuperAdmin={isSuperAdminUser}
          />
        </div>

        <main className={`flex-1 transition-layout ${isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
          <div className="p-6 max-w-4xl mx-auto">
            <div className="mb-6">
              <BreadcrumbTrail />
            </div>

            <div className="mb-8 flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-semibold text-foreground mb-2">
                  Payment Status
                </h1>
                <p className="text-muted-foreground">
                  Track your subscription payment verification
                </p>
              </div>
              <Button
                onClick={handleCheckAgain}
                variant="outline"
                iconName="RefreshCw"
                disabled={syncing}
                loading={syncing}
              >
                Refresh
              </Button>
            </div>

            {/* Sync Status */}
            <div className="mb-6 p-3 bg-muted/50 rounded-md flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                Last updated: {lastUpdateTime.toLocaleTimeString()}
              </span>
              <span className="text-success">Online</span>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-md">
                <p className="text-destructive text-sm">{error}</p>
              </div>
            )}

            {successMessage && (
              <div className="mb-6 p-4 bg-success/10 border border-success/20 rounded-md">
                <p className="text-success text-sm">{successMessage}</p>
              </div>
            )}

            {/* Status Card */}
            <div className={`mb-6 p-6 rounded-lg border-2 ${getStatusColor()}`}>
              <div className="flex items-center gap-4">
                {getStatusIcon()}
                <div className="flex-1">
                  <h2 className="text-xl font-semibold mb-1">{getStatusText()}</h2>
                  {paymentData.payment_status === 'rejected' && paymentData.rejection_reason && (
                    <p className="text-sm mt-2">
                      <strong>Reason:</strong> {paymentData.rejection_reason}
                    </p>
                  )}
                  {paymentData.payment_status === 'approved' && paymentData.approved_at && (
                    <p className="text-sm mt-2">
                      Approved on {new Date(paymentData.approved_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Payment Details */}
            <div className="mb-6 bg-card rounded-lg border border-border shadow-elevation-1 p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4">Payment Details</h3>
              {paymentSummary && paymentSummary.paymentCount > 1 ? (
                <div className="mb-4 p-4 bg-muted/30 rounded-md border border-border">
                  <p className="text-sm text-muted-foreground mb-2">Total for {paymentSummary.paymentCount} pending payment(s):</p>
                  <p className="text-2xl font-semibold text-foreground">{paymentSummary.totalAmount.toLocaleString()} VT</p>
                  <div className="mt-3 space-y-2">
                    {paymentSummary.payments.map((p) => (
                      <div key={p.id} className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">
                          {p.group_name || 'Group'}: {p.amount_vatu.toLocaleString()} VT
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Amount</span>
                  <span className="font-semibold text-foreground">
                    {paymentSummary ? paymentSummary.totalAmount.toLocaleString() : paymentData.amount_vatu.toLocaleString()} VT
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Reference Number</span>
                  <span className="font-mono text-foreground">{paymentData.payment_reference}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Submitted</span>
                  <span className="text-foreground">
                    {new Date(paymentData.submitted_at).toLocaleDateString()}
                  </span>
                </div>
                {paymentData.plan && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Plan</span>
                    <span className="text-foreground">{paymentData.plan.plan_name}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Receipt Status */}
            {paymentData.payment_proof_url && (
              <div className="mb-6 p-4 bg-success/10 border border-success/20 rounded-md">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-success" />
                  <span className="text-sm font-medium text-success">
                    Receipt uploaded - Payment under review
                  </span>
                </div>
              </div>
            )}

            {/* Upload Receipt Section (for pending payments) */}
            {paymentData.payment_status === 'pending' && (
              <div className="mb-6 bg-card rounded-lg border border-border shadow-elevation-1 p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4">Upload Payment Receipt</h3>
                
                {paymentData.payment_proof_url ? (
                  <div className="space-y-4">
                    <div className="p-4 bg-success/10 border border-success/20 rounded-md">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle className="w-5 h-5 text-success" />
                        <span className="text-sm font-medium text-success">
                          Receipt has been uploaded
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Your payment receipt has been submitted and is awaiting administrator review.
                      </p>
                    </div>
                    <div className="border-2 border-dashed border-muted rounded-md p-6 text-center opacity-50 cursor-not-allowed">
                      <input
                        type="file"
                        id="receipt-upload"
                        accept="image/*,application/pdf"
                        onChange={handleFileSelect}
                        className="hidden"
                        disabled
                      />
                      <label
                        htmlFor="receipt-upload"
                        className="flex flex-col items-center gap-2 cursor-not-allowed"
                      >
                        <Upload className="w-8 h-8 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">
                          Receipt already uploaded (disabled)
                        </span>
                        <span className="text-xs text-muted-foreground">
                          Cannot upload another receipt
                        </span>
                      </label>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {!receiptFile ? (
                      <div className="border-2 border-dashed border-border rounded-md p-6 text-center hover:border-primary/50 transition-colors">
                        <input
                          type="file"
                          id="receipt-upload"
                          accept="image/*,application/pdf"
                          onChange={handleFileSelect}
                          className="hidden"
                        />
                        <label
                          htmlFor="receipt-upload"
                          className="cursor-pointer flex flex-col items-center gap-2"
                        >
                          <Upload className="w-8 h-8 text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">
                            Click to upload receipt (JPG, PNG, or PDF)
                          </span>
                          <span className="text-xs text-muted-foreground">
                            Max file size: 5MB
                          </span>
                        </label>
                      </div>
                    ) : (
                      <div className="border border-border rounded-md p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <FileImage className="w-5 h-5 text-muted-foreground" />
                            <div>
                              <p className="text-sm font-medium text-foreground">{receiptFile.name}</p>
                              <p className="text-xs text-muted-foreground">
                                {(receiptFile.size / 1024).toFixed(2)} KB
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={handleRemoveFile}
                            className="p-1 hover:bg-muted rounded transition-colors"
                          >
                            <X className="w-4 h-4 text-muted-foreground" />
                          </button>
                        </div>
                        {receiptPreview && (
                          <div className="mt-4 border border-border rounded-md overflow-hidden">
                            <img
                              src={receiptPreview}
                              alt="Receipt preview"
                              className="w-full h-auto max-h-64 object-contain"
                            />
                          </div>
                        )}
                      </div>
                    )}

                    {receiptFile && (
                      <Button
                        onClick={handleUploadReceipt}
                        loading={uploading}
                        disabled={uploading}
                        iconName="Upload"
                        iconPosition="right"
                        fullWidth
                      >
                        Upload Receipt
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="space-y-3">
              {paymentData.payment_status === 'pending' && (
                <>
                  {!paymentData.payment_proof_url && (
                    <Button
                      onClick={handleCancelPayment}
                      variant="outline"
                      iconName="X"
                      fullWidth
                    >
                      Cancel Payment
                    </Button>
                  )}
                </>
              )}

              {paymentData.payment_status === 'approved' && (
                <Button
                  onClick={() => navigate(userRole === 'admin' ? '/admin-dashboard' : '/member-dashboard')}
                  variant="default"
                  iconName="LayoutDashboard"
                  iconPosition="right"
                  fullWidth
                >
                  Go to Dashboard
                </Button>
              )}

              {paymentData.payment_status === 'rejected' && (
                <>
                  <Button
                    onClick={handleResubmitPayment}
                    variant="default"
                    iconName="RefreshCw"
                    iconPosition="right"
                    fullWidth
                  >
                    Resubmit Payment
                  </Button>
                  <Button
                    onClick={handleChangePlan}
                    variant="outline"
                    iconName="Package"
                    fullWidth
                  >
                    Change Plan
                  </Button>
                </>
              )}
            </div>

            {/* Important Notes */}
            <div className="mt-6 p-4 bg-warning/10 border border-warning/20 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
                <div className="text-sm text-foreground">
                  <p className="font-semibold mb-1 text-foreground">Important:</p>
                  <ul className="list-disc list-inside space-y-1 ml-2 text-foreground">
                    <li>Payment verification typically takes 1-2 business days</li>
                    <li>You'll receive a notification once your payment is approved or rejected</li>
                    <li>Make sure your receipt clearly shows the payment reference number</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Upload Progress Overlay */}
      {uploading && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg p-6 max-w-sm w-full mx-4">
            <div className="flex flex-col items-center gap-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              <p className="text-foreground">Uploading receipt...</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentStatusPage;

